import {
  CustomGetCompaniesForFilterDocument,
  GetProposalsSortKey,
  ProposalStatus,
  SortDirection,
  useCustomGetProposalsQuery,
} from "generated/graphql";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import usePaginatedResources from "hooks/usePaginatedResources";
import { useEffect, useMemo } from "react";
import { sortDirectionMap } from "constants/sortDirectionMap";
import MDBox from "components/MDBox";
import { IconButton, InputBase, Paper } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import usePaginatedCompanyLookup from "hooks/companies/usePaginatedCompanyLookup";
import useGetAllOrganizationUsersNonEmployee from "hooks/users/useGetAllOrganizationUsersNonEmployee";

export const proposalsSortKeyMap = {
  id: GetProposalsSortKey.ID,
  company: GetProposalsSortKey.COMPANY,
  status: GetProposalsSortKey.STATUS,
  createdAt: GetProposalsSortKey.CREATED,
  sentAt: GetProposalsSortKey.SENT,
  overallTotal: GetProposalsSortKey.TOTAL,
  stage: GetProposalsSortKey.STAGE,
  default: GetProposalsSortKey.CREATED,
};

export const proposalsInitialSortDirectionKey = "desc";
export const proposalsInitialSortDirectionValue = SortDirection.DESC;
export const proposalsInitialSortKeyKey = "createdAt";
export const proposalsInitialSortKeyValue = GetProposalsSortKey.CREATED;

type FiltersProps = {
  defaultValue: any;
  onQueryChange: (v: any) => void;
  sortKeyChanged: boolean;
  setSort: (v: any) => void;
  sortDirection: any;
  rightSlot?: React.ReactNode;
};

const Filters = ({
  defaultValue,
  onQueryChange,
  sortKeyChanged,
  setSort,
  sortDirection,
  rightSlot,
}: FiltersProps) => {
  return (
    <MDBox position="relative" zIndex={2}>
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Paper
          component="form"
          sx={{
            p: "2px 4px",
            display: "flex",
            alignItems: "center",
            width: { xs: "100%", sm: 420 },
          }}
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <InputBase
            autoFocus
            sx={{ ml: 1, flex: 1, fontSize: "14px" }}
            placeholder="Search Proposals"
            value={defaultValue}
            inputProps={{ "aria-label": "search proposals" }}
            onChange={({ currentTarget }: any) => {
              onQueryChange(currentTarget.value);
              if (!sortKeyChanged) {
                setSort({
                  sortDirection: proposalsInitialSortDirectionKey,
                  sortKey: proposalsInitialSortKeyKey,
                });
              }
            }}
          />
          <IconButton type="button" sx={{ p: "10px" }} aria-label="search">
            <SearchIcon />
          </IconButton>
        </Paper>

        {/* This is where the Create Proposal button lives now */}
        {rightSlot && <MDBox>{rightSlot}</MDBox>}
      </MDBox>
    </MDBox>
  );
};

export default function useProposalsTableData(options?: { rightSlot?: React.ReactNode }) {
  const savedFilters = JSON.parse(localStorage.getItem("ListProposalsTableFilters")) ?? null;
  const { filtering, sorting, offset } = usePaginatedVariables({
    initialSortDirection: proposalsInitialSortDirectionKey,
    initialSortKey: proposalsInitialSortKeyKey,
    initialStatuses: savedFilters?.statuses,
    initialMultipleCompanyIds: savedFilters?.multipleCompanyIDs,
    initialMultipleUserIDs: savedFilters?.multipleUserIDs,
    initialSearchTerm: savedFilters?.searchTerm ?? null,
  });

  const sortKeyChanged = useMemo(() => {
    return (
      proposalsInitialSortDirectionKey !== sorting.sortDirection ||
      proposalsInitialSortKeyKey !== sorting.sortKey
    );
  }, [
    proposalsInitialSortDirectionKey,
    proposalsInitialSortKeyKey,
    sorting.sortKey,
    sorting.sortDirection,
  ]);

  const {
    loading: companiesLoading,
    items: companiesData,
    error: companiesError,
    search: companiesSearch,
    loadInitialPage: loadInitialCompanies,
    loadMore: loadMoreCompanies,
    resetSearch: resetSearchCompanies,
    hasMorePages: companiesHasMorePages,
  } = usePaginatedCompanyLookup<{ id: string; name: string }>(
    CustomGetCompaniesForFilterDocument,
    filtering.multipleCompanyIDs ?? []
  );

  const {
    loading: organizationUsersLoading,
    data: organizationUsersData,
    error: organizationUsersError,
  } = useGetAllOrganizationUsersNonEmployee();

  const {
    loading,
    data: graphqlData,
    error,
    startPolling,
    stopPolling,
  } = useCustomGetProposalsQuery({
    variables: {
      search: filtering.debouncedSearchTerm,
      sortDirection: sortDirectionMap[sorting.sortDirection],
      sortKey: sorting.sortKey
        ? proposalsSortKeyMap[sorting.sortKey.toLowerCase()] || proposalsInitialSortKeyValue
        : null,
      statuses: filtering.statuses as ProposalStatus[],
      first: offset.first,
      page: offset.page,
      companyId: filtering.companyId ?? null,
      userId: filtering.userID ?? null,
      multipleCompanyIDs: filtering.multipleCompanyIDs ?? null,
      multipleUserIDs: filtering.multipleUserIDs ?? null,
    },
    fetchPolicy: "no-cache",
  });

  const data = graphqlData?.getProposals.data;
  const hasActivePackageSend = data?.some((proposal) =>
    ["QUEUED", "PROCESSING"].includes(proposal.latestPackageSendRun?.status ?? "")
  );

  useEffect(() => {
    if (hasActivePackageSend) {
      startPolling(3000);
      return stopPolling;
    }

    stopPolling();
  }, [hasActivePackageSend, startPolling, stopPolling]);

  const pagination = usePaginatedResources({
    paginate: offset.paginate,
    paginatorInfo: graphqlData?.getProposals?.paginatorInfo,
  });

  const columns = useMemo(() => {}, []);

  const filterControl = (
    <Filters
      defaultValue={filtering.searchTerm}
      onQueryChange={filtering.setSearchTerm}
      sortKeyChanged={sortKeyChanged}
      setSort={sorting.setSort}
      sortDirection={sorting.sortDirection}
      rightSlot={options?.rightSlot}
    />
  );

  const renderRowSubComponent = (row: any) => <pre>{JSON.stringify(row, undefined, 2)}</pre>;

  const tableData = useMemo(() => {
    return { columns, rows: data };
  }, [columns, data]);

  return {
    data,
    loading,
    error,
    columns,
    tableData,
    filterControl,
    renderRowSubComponent,
    pagination,
    filtering,
    sorting,
    paginatorInfo: pagination.paginatorInfo,
    companiesData,
    companiesLoading,
    companiesError,
    companiesSearch,
    loadInitialCompanies,
    loadMoreCompanies,
    resetSearchCompanies,
    companiesHasMorePages,
    organizationUsersLoading,
    organizationUsersData,
    organizationUsersError,
  } as const;
}
